// ILF Variant Picker — <ilf-product-texture-slider>
// The texture strip is a Swiper (1:1 finger-follow drag, rubber-banding);
// the radios stay the single source of truth: strip swipes .click() the
// matching radio, radio changes slide the strip. The tab knob keeps reading
// data-active-index from CSS.
//
// Two markup variants share this element:
// - selectable (ilf-product-option-texture): tabs are the radios.
// - orifice-linked (ilf-product-option-texture-linked): orifice cards are the
//   radios; dot indicators + the "Texture · NAME" caption follow the checked
//   radio. The strip slides identically in both variants.

if (!customElements.get('ilf-product-texture-slider')) {
  class IlfProductTextureSlider extends HTMLElement {
    connectedCallback() {
      // Guard against re-entry: connectedCallback can fire more than once
      if (this.initialised) return;
      if (typeof Swiper === 'undefined') {
        window.addEventListener('load', () => this.connectedCallback(), { once: true });
        return;
      }
      this.initialised = true;

      this.onChange = this.onChange.bind(this);
      this.onStripClick = this.onStripClick.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onLensEnter = this.onLensEnter.bind(this);
      this.onLensMove = this.onLensMove.bind(this);
      this.onLensLeave = this.onLensLeave.bind(this);
      this.onLensPointerDown = this.onLensPointerDown.bind(this);

      // Radio change (tab click, or any programmatic .click()) → move strip/knob
      this.addEventListener('change', this.onChange);

      // Dot indicators (linked mode) select the matching radio.
      this.addEventListener('click', this.onDotClick);

      this.strip = this.querySelector('.texture-strip');
      if (this.strip) {
        const swipeEnabled = this.dataset.stripSwipe !== 'false';
        this.stripSwiper = new Swiper(this.strip, {
          slidesPerView: 1,
          spaceBetween: 0,
          speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300,
          initialSlide: Math.max(0, this.activeIndex),
          // Swiping off: the strip becomes a static zoom trigger.
          allowTouchMove: swipeEnabled,
          // Swiper's default threshold is 0: a 1-2px jitter during a click
          // counts as a drag, clears allowClick, and the zoom modal silently
          // refuses to open. Real drags easily exceed 6px.
          threshold: 6,
          watchOverflow: true,
          // Never fire slideChange during construction: the handler selects
          // radios, and at construction time the selection is already correct.
          runCallbacksOnInit: false,
          on: {
            // Swiper passes its instance — the this.stripSwiper property is
            // not assigned yet if the event ever fires during construction.
            slideChange: (swiper) => {
              // Drag/swipe landed on a new slide → check the matching radio so
              // the normal variant-selects flow (price, badges) runs. Guarded:
              // programmatic slideTo on an already-checked radio is a no-op.
              const radios = this.radios;
              const index = swiper.activeIndex ?? 0;
              if (radios[index] && !radios[index].checked) radios[index].click();
              this.updateLensImage();
            },
          },
        });

        // A plain (non-drag) click anywhere on the strip opens the zoom modal
        // in both modes — Swiper's allowClick separates clicks from drags.
        this.strip.addEventListener('click', this.onStripClick);

        // Magnifier lens listeners are always attached; the width gate lives
        // in onLensEnter so resizing across 750px works both ways without a
        // reload. Below 750px the strip shows the static label instead.
        this.strip.addEventListener('pointerenter', this.onLensEnter);
        this.strip.addEventListener('pointermove', this.onLensMove);
        this.strip.addEventListener('pointerleave', this.onLensLeave);
        this.strip.addEventListener('pointerdown', this.onLensPointerDown);
      }

      this.syncToChecked();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onDotClick);
      if (this.strip) {
        this.strip.removeEventListener('click', this.onStripClick);
        this.strip.removeEventListener('pointerenter', this.onLensEnter);
        this.strip.removeEventListener('pointermove', this.onLensMove);
        this.strip.removeEventListener('pointerleave', this.onLensLeave);
        this.strip.removeEventListener('pointerdown', this.onLensPointerDown);
      }
      cancelAnimationFrame(this.lensFrame);
      this.stripSwiper?.destroy(true, true);
      this.stripSwiper = null;
      this.initialised = false;
    }

    onDotClick(event) {
      const dot = event.target.closest('.linked-dot');
      if (!dot) return;
      const index = Number(dot.dataset.dotIndex);
      const radios = this.radios;
      if (radios[index] && !radios[index].checked) radios[index].click();
    }

    onStripClick(event) {
      // Clicks on the zoom button itself already open the modal — forwarding
      // them again would double-toggle it.
      if (event.target.closest('.zoom-open')) return;
      // Swipe mode: Swiper clears allowClick after a real drag — don't open on
      // drag ends. Swipe-off mode must NOT consult allowClick: with
      // allowTouchMove:false Swiper sets it false on ANY pointer movement,
      // bypassing the threshold, which silently swallowed clicks.
      const swipeEnabled = this.dataset.stripSwipe !== 'false';
      if (swipeEnabled && this.stripSwiper && this.stripSwiper.allowClick === false) return;
      this.querySelector('.zoom-open')?.click();
    }

    // ---- Magnifier lens -----------------------------------------------------
    // The lens circle follows the cursor and magnifies the active texture.
    // JS feeds ONLY coordinates and the image URL through CSS custom
    // properties — a documented deviation from the classes-only rule (CSS has
    // no access to cursor positions); every visual rule stays in the CSS file.

    onLensEnter(event) {
      if (event.pointerType !== 'mouse') return;
      // Lens is a wide-viewport affordance; below 750px the static label is
      // the fullscreen hint (checked per event so window resizes just work).
      if (!window.matchMedia('(min-width: 750px)').matches) return;
      this.updateLensImage();
      // Magnification: the lens background renders the texture at 2.4× the
      // width the strip displays it at (design's zoomLevel default; percent
      // background-size would be relative to the lens and could end up
      // SMALLER than the strip).
      const rect = this.strip.getBoundingClientRect();
      if (rect.width) this.style.setProperty('--lens-bg-w', `${Math.round(rect.width * 2.4)}px`);
      this.setLensPosition(event);
      this.classList.add('lens-active');
    }

    onLensMove(event) {
      if (!this.classList.contains('lens-active')) return;
      cancelAnimationFrame(this.lensFrame);
      this.lensFrame = requestAnimationFrame(() => this.setLensPosition(event));
    }

    onLensLeave() {
      cancelAnimationFrame(this.lensFrame);
      this.classList.remove('lens-active');
    }

    onLensPointerDown() {
      // Hide the lens while dragging; it returns on the next pointer move.
      this.classList.remove('lens-active');
    }

    setLensPosition(event) {
      // The background sample point is strip-relative; the lens itself lives on
      // the host (outside the strip's overflow clipping), so its position maps
      // the strip-relative point into the host's coordinate space. In the
      // selectable layout the two origins coincide (offset 0); in the linked
      // layout the orifice cards above the strip make the host origin sit
      // higher than the strip's.
      const rect = this.strip.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const hostRect = this.getBoundingClientRect();
      const x = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
      const y = Math.min(Math.max(event.clientY - rect.top, 0), rect.height);
      this.style.setProperty('--lens-x', `${x + rect.left - hostRect.left}px`);
      this.style.setProperty('--lens-y', `${y + rect.top - hostRect.top}px`);
      this.style.setProperty('--lens-bx', `${(x / rect.width) * 100}%`);
      this.style.setProperty('--lens-by', `${(y / rect.height) * 100}%`);
      // Re-show after a drag once the pointer moves again.
      if (!this.classList.contains('lens-active')) {
        this.classList.add('lens-active');
      }
    }

    updateLensImage() {
      if (!this.strip) return;
      const img = this.strip.querySelector('.swiper-slide-active img');
      const url = img?.currentSrc || img?.src;
      if (url) this.style.setProperty('--lens-image', `url("${url}")`);
    }

    get radios() {
      // The fieldset holds exactly this option's radios — in the tabs, or on
      // the orifice cards in linked mode.
      return [...this.querySelectorAll('input[type="radio"]')];
    }

    get activeIndex() {
      return this.radios.findIndex((radio) => radio.checked);
    }

    syncToChecked() {
      const index = this.activeIndex;
      if (index < 0) return;
      this.dataset.activeIndex = String(index);
      if (this.stripSwiper && this.stripSwiper.activeIndex !== index) {
        this.stripSwiper.slideTo(index);
      }
      this.querySelectorAll('.strip-slide').forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
      });

      // Linked mode: dot indicators and the "Texture · NAME" caption.
      this.querySelectorAll('.linked-dot').forEach((dot, dotIndex) => {
        dot.classList.toggle('active', dotIndex === index);
      });
      const caption = this.querySelector('[data-caption-name]');
      if (caption) {
        const radio = this.radios[index];
        caption.textContent = radio?.dataset.textureName || radio?.value || '';
      }
    }

    onChange() {
      this.syncToChecked();
    }
  }

  customElements.define('ilf-product-texture-slider', IlfProductTextureSlider);
}

// <ilf-product-texture-zoom> — content controller for the texture zoom modal.
// The surrounding <ilf-modal> (ilf-web-components.js) owns open/close; this
// element keeps slides, description, tabs, and heading in sync with the real
// texture radios inside <variant-selects>. The track is a Swiper, created on
// first open (a hidden slider cannot measure itself) with 1:1 drag.
if (!customElements.get('ilf-product-texture-zoom')) {
  class IlfProductTextureZoom extends HTMLElement {
    connectedCallback() {
      // Guard against re-entry: <ilf-modal> re-parents the modal to <body>,
      // which disconnects and reconnects this element.
      if (this.initialised) return;
      this.initialised = true;

      this.onTabClick = this.onTabClick.bind(this);
      this.onRadioChange = this.onRadioChange.bind(this);
      this.onOrientation = this.onOrientation.bind(this);
      this.onModalOpen = this.onModalOpen.bind(this);

      this.querySelectorAll('.texture-tab').forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
      });
      this.querySelectorAll('.orientation-btn').forEach((btn) => {
        btn.addEventListener('click', this.onOrientation);
      });

      this.media = this.querySelector('.zoom-media');

      // IlfModal's own open listener on the trigger was bound first, so by the
      // time this one runs the modal is visible and Swiper can measure.
      this.trigger = document.querySelector(`[data-ilf-modal-open="product-texture-zoom-${this.dataset.sectionId}"]`);
      this.trigger?.addEventListener('click', this.onModalOpen);

      // The picker's radios are the single source of truth.
      this.variantSelects = document.getElementById(`variant-selects-${this.dataset.sectionId}`);
      if (this.variantSelects) {
        this.variantSelects.addEventListener('change', this.onRadioChange);
      }
    }

    disconnectedCallback() {
      if (this.variantSelects) {
        this.variantSelects.removeEventListener('change', this.onRadioChange);
      }
      this.trigger?.removeEventListener('click', this.onModalOpen);
      this.zoomSwiper?.destroy(true, true);
      this.zoomSwiper = null;
      this.initialised = false;
    }

    onModalOpen() {
      if (!this.media || typeof Swiper === 'undefined') return;
      if (this.zoomSwiper) {
        // Re-opened: remeasure and land on the current texture instantly.
        this.zoomSwiper.update();
        this.zoomSwiper.slideTo(Math.max(0, this.activeIndex), 0);
      } else {
        this.zoomSwiper = new Swiper(this.media, {
          slidesPerView: 1,
          spaceBetween: 0,
          speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300,
          initialSlide: Math.max(0, this.activeIndex),
          watchOverflow: true,
          grabCursor: true,
          keyboard: { enabled: true },
          // Never fire slideChange during construction: with a non-zero
          // initialSlide, Swiper would emit it before this.zoomSwiper is even
          // assigned, and the handler would select the wrong texture radio.
          runCallbacksOnInit: false,
          on: {
            // Swiper passes its instance — the this.zoomSwiper property is
            // not assigned yet if the event ever fires during construction.
            slideChange: (swiper) => {
              this.selectIndex(swiper.activeIndex ?? 0);
            },
          },
        });
      }
      this.syncActive();
    }

    get radios() {
      if (!this.variantSelects) return [];
      return [...this.variantSelects.querySelectorAll(
        `input[type="radio"][name="${CSS.escape(this.dataset.optionName)}"]`
      )];
    }

    get activeIndex() {
      return this.radios.findIndex((radio) => radio.checked);
    }

    selectIndex(index) {
      const radios = this.radios;
      if (radios[index] && !radios[index].checked) radios[index].click();
    }

    syncActive() {
      const index = this.activeIndex;
      if (index < 0) return;
      this.dataset.activeIndex = String(index);

      // Swiper owns the track position; slides only need aria-hidden.
      // Descriptions and tabs toggle .active — re-showing a description
      // restarts its entrance animation.
      if (this.zoomSwiper && this.zoomSwiper.activeIndex !== index) {
        this.zoomSwiper.slideTo(index);
      }
      this.querySelectorAll('.zoom-slide').forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
      });
      const toggleActive = (nodes) => {
        nodes.forEach((node, nodeIndex) => {
          node.classList.toggle('active', nodeIndex === index);
        });
      };
      toggleActive([...this.querySelectorAll('.zoom-desc')]);
      toggleActive([...this.querySelectorAll('.texture-tab')]);

      const name = this.querySelector('[data-zoom-name]');
      const radio = this.radios[index];
      // Linked-mode radios carry data-texture-name (the value without its
      // parenthetical part); selectable radios fall back to the raw value.
      const value = radio?.dataset.textureName || radio?.value;
      if (name && value) name.textContent = value;
    }

    onTabClick(event) {
      const value = event.currentTarget.dataset.zoomValue;
      const index = this.radios.findIndex((radio) => radio.value === value);
      if (index >= 0) this.selectIndex(index);
    }

    onRadioChange() {
      this.syncActive();
    }

    onOrientation(event) {
      const vertical = event.currentTarget.classList.contains('orientation-btn--v');
      this.classList.toggle('vertical', vertical);
      this.querySelector('.orientation-btn--h')?.classList.toggle('active', !vertical);
      this.querySelector('.orientation-btn--v')?.classList.toggle('active', vertical);
      // Slide dimensions changed between the two layouts — remeasure.
      this.zoomSwiper?.update();
    }
  }

  customElements.define('ilf-product-texture-zoom', IlfProductTextureZoom);
}

// <ilf-product-orifice-cards> — mirrors the selected orifice letter onto the
// section's <media-gallery> as data-ilf-selected-orifice. CSS in
// ilf-product-option-picker.css then hides gallery media tagged (via
// data-ilf-media-orifice, rendered from SKU-prefixed file names) with a
// different orifice. Untagged media is never hidden.
if (!customElements.get('ilf-product-orifice-cards')) {
  class IlfProductOrificeCards extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.onChange = this.onChange.bind(this);
      this.addEventListener('change', this.onChange);
      this.syncGallery();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.initialised = false;
    }

    onChange() {
      this.syncGallery();
    }

    syncGallery() {
      const gallery = document.getElementById(`MediaGallery-${this.dataset.sectionId}`);
      if (!gallery) return;

      const checked = this.querySelector('input[type="radio"]:checked');
      const letter = checked?.dataset.ilfOrificeLetter;
      if (letter) {
        gallery.setAttribute('data-ilf-selected-orifice', letter);
        this.preloadOrificeMedia(gallery, letter);
      } else {
        gallery.removeAttribute('data-ilf-selected-orifice');
      }
    }

    // Start fetching the incoming orifice's images immediately, so they are
    // ready (or already in flight) by the time the gallery switches to them.
    // The gallery loads only the initial slide and its neighbours eagerly;
    // every other slide image still carries loading="lazy".
    preloadOrificeMedia(gallery, letter) {
      gallery
        .querySelectorAll(`[data-ilf-media-orifice="${letter}"] img[loading="lazy"]`)
        .forEach((img) => {
          if (!img.complete) img.loading = 'eager';
        });
    }
  }

  customElements.define('ilf-product-orifice-cards', IlfProductOrificeCards);
}

// <ilf-product-case-preview> — animated case preview above the Case option's
// pills (ilf-product-option-case.liquid). The checked radio's data-case-state
// drives the without-case class; every transition lives in the CSS.
if (!customElements.get('ilf-product-case-preview')) {
  class IlfProductCasePreview extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.onChange = this.onChange.bind(this);
      this.addEventListener('change', this.onChange);
      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.initialised = false;
    }

    onChange() {
      this.sync();
    }

    sync() {
      const checked = this.querySelector('input[type="radio"]:checked');
      this.classList.toggle('without-case', checked?.dataset.caseState === 'without');
    }
  }

  customElements.define('ilf-product-case-preview', IlfProductCasePreview);
}
